// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { createAsyncThunk, type GetThunkAPI } from '@reduxjs/toolkit';
import { fetchAiApi } from '@advisor-app/api/ai-api';
import { screenerApi } from '@shared/data-access/api/ai/screener-api';
import { type SearchAssetClass } from '@shared/data-access/interfaces/api/ai/asset-allocation';
import { type OptimizationObjective } from '@shared/data-access/interfaces/api/ai/portfolio';
import { segmentsFormatted } from '@shared/ui-kit/utils/segments-formatted';

export const getUniverseData = async (
  id: string | undefined,
  api: GetThunkAPI<{}>,
) => {
  const universesPromise = api
    .dispatch(screenerApi.endpoints.getStockUniverseBase.initiate())
    .unwrap();

  const sectorsPromise = api
    .dispatch(
      screenerApi.endpoints.getSettingsByType.initiate({
        assetType: 'stock',
        settingType: 'sectors',
      }),
    )
    .unwrap();

  const factorsPromise = api
    .dispatch(
      screenerApi.endpoints.getSettingsByType.initiate({
        assetType: 'stock',
        settingType: 'factors',
      }),
    )
    .unwrap();

  const [universesResp, sectorsResp, factorsResp] = await Promise.all([
    universesPromise,
    sectorsPromise,
    factorsPromise,
  ]);

  const sectorsFiltered = sectorsResp.filter(sector => sector !== 'N/A');

  const factorsWithRange = factorsResp.map(factor => ({
    factor,
    ranges: [0, 100],
  }));

  return {
    universes: universesResp,
    sectors: sectorsFiltered,
    factors_filter: factorsWithRange,
    factors: factorsResp,
    id,
  };
};

export const fetchAvailableUniverseData = createAsyncThunk(
  'setAvailableParameters/universe',
  getUniverseData,
);

export const getRelabanceData = async (_: void, api: GetThunkAPI<{}>) => {
  const benchmarkPromise = api
    .dispatch(screenerApi.endpoints.getStockBenchmarks.initiate())
    .unwrap();

  const stockObjectivesPromise = fetchAiApi<OptimizationObjective[]>({
    endpoint: '/portfolio/stock/optimization/settings/objectives/',
    method: 'GET',
  });

  const etfObjectivesPromise = fetchAiApi<OptimizationObjective[]>({
    endpoint: '/portfolio/etf/optimization/settings/objectives/',
    method: 'GET',
  });

  const mutualFundObjectivesPromise = fetchAiApi<OptimizationObjective[]>({
    endpoint: '/portfolio/mutual_fund/optimization/settings/objectives/',
    method: 'GET',
  });

  const [benchmarkResp, stockObjResp, etfObjResp, mutualFundObjResp] =
    await Promise.all([
      benchmarkPromise,
      stockObjectivesPromise,
      etfObjectivesPromise,
      mutualFundObjectivesPromise,
    ]);

  return {
    stock_objectives: stockObjResp.data,
    etf_objectives: etfObjResp.data,
    mutual_fund_objectives: mutualFundObjResp.data,
    // crypto_objectives: cryptoObjResp.data,
    benchmarks: benchmarkResp,
  };
};

export const fetchStaticRebalanceData = createAsyncThunk(
  'setAvailableParameters/rebalance',
  getRelabanceData,
);

export const getAssetClasses = async () => {
  const resp = await fetchAiApi<SearchAssetClass[]>({
    endpoint: '/asset_allocation/asset_class/search/',
    method: 'POST',
    payload: {},
  });

  const formattedData = resp.data.map(item => {
    return {
      asset_category: item.asset_category,
      asset_class: segmentsFormatted(item.asset_class),
      signal_ticker: item.signal_ticker,
      etf: item.etf,
    };
  });

  return formattedData;
};

export const fetchAssetClasses = createAsyncThunk(
  'setAssetAllocation/assetClasses',
  getAssetClasses,
);
